/**
 * Locale fragments (CONTRACT §6): strings a feature area or the guidance
 * module adds, or overrides, without editing the global dictionaries.
 *
 * A fragment is `src/features/<area>/locales/{en,ar}.json` or
 * `src/guidance/locales/{en,ar}.json`. It is deep-merged over the global
 * resources before i18next initialises. At integration the lead folds the
 * overrides into the global files, so the final state has none.
 */
export interface Messages {
  [key: string]: string | Messages;
}

export interface LocaleFragment {
  /** The source file, e.g. `/src/guidance/locales/ar.json`. */
  path: string;
  /** The file's base name, e.g. `ar`. Only a supported locale is merged. */
  locale: string;
  messages: Messages;
}

/** Reads the fragment modules a glob returned, in a stable (path) order. */
export function collectFragments(modules: Record<string, Messages>): LocaleFragment[] {
  return Object.keys(modules)
    .sort()
    .map((path) => ({
      path,
      locale: path.slice(path.lastIndexOf('/') + 1).replace(/\.json$/, ''),
      messages: modules[path],
    }));
}

function isMessages(value: unknown): value is Messages {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * `overlay` deep-merged over `base`, as a new object; neither is changed.
 * Where both hold a branch, the branches merge; anywhere else the overlay wins.
 */
export function mergeMessages(base: Messages, overlay: Messages): Messages {
  const merged: Messages = { ...base };

  for (const [key, value] of Object.entries(overlay)) {
    const current = merged[key];
    merged[key] = isMessages(current) && isMessages(value) ? mergeMessages(current, value) : value;
  }

  return merged;
}

/** Every leaf key, dotted (`guidance.tours.T01.title`). */
export function flattenKeys(messages: Messages, prefix = ''): string[] {
  return Object.entries(messages).flatMap(([key, value]) =>
    isMessages(value) ? flattenKeys(value, `${prefix}${key}.`) : [`${prefix}${key}`],
  );
}
